// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SelectOptionsModal from "@awc/App/Components/Product/SelectOptionsModal";
import xLoadingOverlay from "@awc/App/Handlers/XNG/LoadingOverlay";
import { isProductQuoteRequired } from "@awc/App/Store/Selectors/ProductSelectors";
import { mergeWithCss } from "@awc/XDL/Utilities";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import React, { FC, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    extendedStyles?: ProductBuyButtonStyles["button"];
    isTableView?: boolean;
    isPlp?: boolean;
}

const mapDispatchToProps = {
    setProduct,
    xLoadingOverlay,
};

type Props = OwnProps & HasProductContext & ResolveThunks<typeof mapDispatchToProps>;

interface ProductBuyButtonStyles {
    button?: ButtonPresentationProps;
}

export const productBuyButtonStyles: ProductBuyButtonStyles = {};

const ProductBuyButton: FC<Props> = ({
    productContext: { product, productInfo },
    extendedStyles,
    setProduct,
    xLoadingOverlay,
    isTableView,
    isPlp,
}) => {
    const [styles] = useState(() => mergeWithCss(productBuyButtonStyles.button, extendedStyles));
    const [isOpen, setIsOpen] = useState(false);
    const handleBuyNow = () => {
        if (product.isVariantParent && product.variantTraits?.length) {
            xLoadingOverlay({ key: "add", id: "select_product_options_modal" });
            setProduct({
                productId: product.id,
                validateProduct: true,
                skipInventoryValidation: false,
                pricing: productInfo.pricing,
                isProductSelectorVariantModal: true,
                onComplete: () => {
                    xLoadingOverlay({ key: "clear", id: "select_product_options_modal" });
                },
            } as any);
            return;
        }
        setIsOpen(true);
    };

    const productQuoteRequired = isProductQuoteRequired(product, productInfo);

    const notBuyable = useMemo(() => {
        if (isPlp) {
            if (isTableView) {
                return (productQuoteRequired || !product.canAddToCart) && !product.variantTraits?.length;
            }
            return productQuoteRequired || (!product.canAddToCart && !product.variantTraits?.length);
        }
        return productQuoteRequired;
    }, [isPlp, isTableView, productQuoteRequired, product.canAddToCart, product.variantTraits?.length]);

    if (notBuyable || !productInfo?.pricing || !productInfo?.pricing?.unitNetPrice) {
        return null;
    }

    return (
        <>
            <Button {...styles} onClick={handleBuyNow} data-test-selector="ProductBuyButton">
                {translate("BUY NOW")}
            </Button>
            {!product.variantTraits?.length && (
                <SelectOptionsModal isOpen={isOpen} closeModalHandler={() => setIsOpen(false)} />
            )}
        </>
    );
};

export default withProductContext(connect(null, mapDispatchToProps)(ProductBuyButton));
